import type { ThemeColorStop, ThemeConfig } from "schemas/generated/theme";

// The user-facing choice: "light" | "dark" | "system". "system" defers to the
// device's prefers-color-scheme.
export type ThemeMode = ThemeConfig["mode"];

// The concrete mode used for rendering. "system" is always resolved to one of
// these before the palette or any UI branches on it.
export type EffectiveThemeMode = "light" | "dark";

export function resolveEffectiveMode({
  mode,
  prefersDark,
}: {
  mode: ThemeMode;
  prefersDark: boolean;
}): EffectiveThemeMode {
  if (mode === "system") {
    return prefersDark ? "dark" : "light";
  }
  return mode;
}

export type ChartScheme = NonNullable<ThemeConfig["chart"]>["scheme"];

export const CHART_SCHEMES: ChartScheme[] = [
  "complementary",
  "splitComplementary",
  "triadic",
  "tetradic",
  "analogous",
  "monochromatic",
];

// Contrastful by default so a single brand hue never renders as five shades of
// itself — series get the seed's harmonic opposites, not a monochrome wash.
export const DEFAULT_CHART_SCHEME: ChartScheme = "splitComplementary";

// Shared default rendered whenever a consumer has no saved theme config.
// Lives alongside the palette generator so both surface (editor home tab,
// settings miniapp) and orchestrator (AgentThemeShell) draw from
// one source of truth.
export const DEFAULT_THEME_CONFIG: ThemeConfig = {
  colors: [{ hue: 72, chroma: 12, x: 72 / 360, y: 1 - 12 / 100 }],
  mode: "light",
  intensity: 75,
};

export interface ThemePalette {
  sheet: string;
  onSheet: string;
  surface: string;
  onSurface: string;
  surfaceWarm: string;
  cardSurface: string;
  hover: string;
  default: string;
  muted: string;
  placeholder: string;
  border: string;
  componentBorder: string;
  primary: string;
  onPrimary: string;
  ring: string;
  chrome: string;
  onChrome: string;
  chromeHover: string;
  chromeBorder: string;
  menuItemHover: string;
  menuItemSelected: string;
  selectableHover: string;
  selectableSelected: string;
  selectableSelectedHover: string;
  buttonPrimaryHover: string;
  buttonSecondaryHover: string;
  buttonTertiaryHover: string;
  // Five categorical chart series, harmonized from the seed via the configured
  // scheme. Index 0 is the primary/dominant series.
  chartSeries: string[];
}

interface ModeCurves {
  sheetL: number;
  surfaceL: number;
  // Nudged off the sheet toward `surface` in both modes, so the softer page tint
  // stays recessed in light and raised in dark rather than inverting.
  surfaceWarmL: number;
  cardSurfaceL: number;
  hoverL: number;
  defaultL: number;
  mutedL: number;
  placeholderL: number;
  borderL: number;
  borderAlpha: number;
  componentBorderAlpha: number;
  primaryL: number;
  chromeL: number;
  chromeHoverL: number;
  chromeBorderL: number;
  chromeBorderAlpha: number;
  chromaScale: number;
}

const MODE_CURVES: Record<EffectiveThemeMode, ModeCurves> = {
  light: {
    sheetL: 0.99,
    surfaceL: 0.96,
    surfaceWarmL: 0.985,
    cardSurfaceL: 0.99,
    hoverL: 0.92,
    defaultL: 0.147,
    mutedL: 0.547,
    placeholderL: 0.714,
    borderL: 0.922,
    borderAlpha: 0.7,
    componentBorderAlpha: 0.85,
    primaryL: 0.214,
    chromeL: 0.986,
    chromeHoverL: 0.96,
    chromeBorderL: 0.922,
    chromeBorderAlpha: 0.7,
    chromaScale: 1,
  },
  dark: {
    sheetL: 0.147,
    surfaceL: 0.214,
    surfaceWarmL: 0.158,
    cardSurfaceL: 0.04,
    hoverL: 0.268,
    defaultL: 0.986,
    mutedL: 0.714,
    placeholderL: 0.547,
    borderL: 1,
    borderAlpha: 0.1,
    componentBorderAlpha: 0.15,
    primaryL: 0.922,
    chromeL: 0.214,
    chromeHoverL: 0.268,
    chromeBorderL: 1,
    chromeBorderAlpha: 0.1,
    chromaScale: 0.85,
  },
};

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function oklch(lightness: number, chroma: number, hue: number): string {
  const clampedLightness = clamp(lightness, 0, 1);
  const clampedChroma = clamp(chroma, 0, 0.4);
  return `oklch(${clampedLightness.toFixed(4)} ${clampedChroma.toFixed(4)} ${hue.toFixed(1)})`;
}

function oklchAlpha(
  lightness: number,
  chroma: number,
  hue: number,
  alpha: number,
): string {
  const clampedLightness = clamp(lightness, 0, 1);
  const clampedChroma = clamp(chroma, 0, 0.4);
  const clampedAlpha = clamp(alpha, 0, 1);
  return `oklch(${clampedLightness.toFixed(4)} ${clampedChroma.toFixed(4)} ${hue.toFixed(1)} / ${clampedAlpha.toFixed(3)})`;
}

function readableForegroundForLightness(
  lightness: number,
  hue: number,
): string {
  return lightness >= 0.55 ? oklch(0.13, 0.006, hue) : oklch(0.97, 0.006, hue);
}

// Hue rotations (degrees from the seed) for each of the five series, per
// scheme. Schemes with fewer than five anchors repeat hues; the lightness
// rungs below pull those repeats apart so every series stays distinguishable.
const SCHEME_HUE_OFFSETS: Record<ChartScheme, number[]> = {
  complementary: [0, 180, 0, 180, 0],
  splitComplementary: [0, 150, 210, 30, 180],
  triadic: [0, 120, 240, 60, 180],
  tetradic: [0, 90, 180, 270, 45],
  analogous: [0, 32, -32, 64, -64],
  monochromatic: [0, 0, 0, 0, 0],
};

// Per-slot lightness rungs guarantee separation even when hues repeat and keep
// series legible against the active mode's background.
const SERIES_LIGHTNESS: Record<EffectiveThemeMode, number[]> = {
  light: [0.55, 0.7, 0.46, 0.63, 0.78],
  dark: [0.66, 0.78, 0.58, 0.72, 0.85],
};

// Chroma tracks the themed chroma (a muted theme yields muted charts) but is
// clamped into a restrained band so no theme can render as a toy.
const SERIES_CHROMA_MULT = [0.95, 0.78, 0.95, 0.78, 0.62];
const SERIES_CHROMA_MIN = 0.045;
const SERIES_CHROMA_MAX = 0.14;

function generateChartSeries({
  hue,
  chromaBase,
  chromaScale,
  mode,
  scheme,
}: {
  hue: number;
  chromaBase: number;
  chromaScale: number;
  mode: EffectiveThemeMode;
  scheme: ChartScheme;
}): string[] {
  const offsets = SCHEME_HUE_OFFSETS[scheme];
  const lightness = SERIES_LIGHTNESS[mode];
  return offsets.map((offset, index) => {
    const seriesHue = (((hue + offset) % 360) + 360) % 360;
    const seriesChroma = clamp(
      chromaBase * SERIES_CHROMA_MULT[index]! * chromaScale,
      SERIES_CHROMA_MIN,
      SERIES_CHROMA_MAX,
    );
    return oklch(lightness[index]!, seriesChroma, seriesHue);
  });
}

export function generatePalette(
  config: ThemeConfig,
  mode: EffectiveThemeMode,
): ThemePalette {
  const { colors, intensity } = config;
  const curves = MODE_CURVES[mode];

  const iScale = intensity / 50;

  const seed = colors[0]!;
  const baseHue = seed.hue;
  const baseChroma = seed.chroma;
  const cs = curves.chromaScale;
  const chromaBase = (baseChroma / 100) * iScale;

  const chartScheme = config.chart?.scheme ?? DEFAULT_CHART_SCHEME;
  const chartHue = config.chart?.seedHue ?? baseHue;

  const sheetL = clamp(curves.sheetL, 0.05, 0.99);
  const surfaceL = clamp(curves.surfaceL, 0.08, 0.99);
  const primaryL = clamp(curves.primaryL, 0.05, 0.98);
  const chromeL = clamp(curves.chromeL, 0.05, 0.99);

  return {
    sheet: oklch(sheetL, chromaBase * 0.03 * cs, baseHue),
    onSheet: readableForegroundForLightness(sheetL, baseHue),
    surface: oklch(surfaceL, chromaBase * 0.06 * cs, baseHue),
    onSurface: readableForegroundForLightness(surfaceL, baseHue),
    surfaceWarm: oklch(
      clamp(curves.surfaceWarmL, 0.05, 0.99),
      chromaBase * 0.04 * cs,
      baseHue,
    ),
    cardSurface: oklch(
      clamp(curves.cardSurfaceL, 0.02, 0.99),
      mode === "dark" ? 0 : chromaBase * 0.03 * cs,
      baseHue,
    ),
    hover: oklch(
      clamp(curves.hoverL, 0.1, 0.99),
      chromaBase * 0.05 * cs,
      baseHue,
    ),
    default: oklch(
      clamp(curves.defaultL, 0.05, 0.99),
      chromaBase * 0.02 * cs,
      baseHue,
    ),
    muted: oklch(
      clamp(curves.mutedL, 0.1, 0.9),
      chromaBase * 0.07 * cs,
      baseHue,
    ),
    placeholder: oklch(
      clamp(curves.placeholderL, 0.15, 0.85),
      chromaBase * 0.04 * cs,
      baseHue,
    ),
    border: oklchAlpha(
      curves.borderL,
      chromaBase * 0.03 * cs,
      baseHue,
      curves.borderAlpha,
    ),
    componentBorder: oklchAlpha(
      curves.borderL,
      chromaBase * 0.035 * cs,
      baseHue,
      curves.componentBorderAlpha,
    ),
    primary: oklch(primaryL, chromaBase * 0.18 * cs, baseHue),
    onPrimary: readableForegroundForLightness(primaryL, baseHue),
    ring: oklch(
      clamp(curves.primaryL - 0.1, 0.2, 0.8),
      chromaBase * 0.08 * cs,
      baseHue,
    ),
    chrome: oklch(chromeL, chromaBase * 0.045 * cs, baseHue),
    onChrome: readableForegroundForLightness(chromeL, baseHue),
    chromeHover: oklch(
      clamp(curves.chromeHoverL, 0.08, 0.96),
      chromaBase * 0.035 * cs,
      baseHue,
    ),
    chromeBorder: oklchAlpha(
      curves.chromeBorderL,
      chromaBase * 0.03 * cs,
      baseHue,
      curves.chromeBorderAlpha,
    ),
    menuItemHover: oklch(
      clamp(sheetL + (mode === "dark" ? 0.06 : -0.02), 0.05, 0.99),
      chromaBase * 0.025 * cs,
      baseHue,
    ),
    menuItemSelected: oklch(
      clamp(sheetL + (mode === "dark" ? 0.1 : -0.035), 0.05, 0.99),
      chromaBase * 0.035 * cs,
      baseHue,
    ),
    selectableHover: oklch(
      clamp(sheetL + (mode === "dark" ? 0.05 : -0.015), 0.05, 0.99),
      chromaBase * 0.02 * cs,
      baseHue,
    ),
    selectableSelected: oklch(
      clamp(sheetL + (mode === "dark" ? 0.08 : -0.025), 0.05, 0.99),
      chromaBase * 0.1 * cs,
      baseHue,
    ),
    selectableSelectedHover: oklch(
      clamp(sheetL + (mode === "dark" ? 0.1 : -0.04), 0.05, 0.99),
      chromaBase * 0.13 * cs,
      baseHue,
    ),
    buttonPrimaryHover: oklch(
      clamp(primaryL + 0.07, 0.05, 0.98),
      chromaBase * 0.18 * cs,
      baseHue,
    ),
    buttonSecondaryHover: oklch(
      clamp(sheetL + (mode === "dark" ? 0.05 : -0.015), 0.05, 0.99),
      chromaBase * 0.025 * cs,
      baseHue,
    ),
    buttonTertiaryHover: oklch(
      clamp(surfaceL + (mode === "dark" ? 0.05 : -0.015), 0.05, 0.99),
      chromaBase * 0.02 * cs,
      baseHue,
    ),
    chartSeries: generateChartSeries({
      hue: chartHue,
      chromaBase,
      chromaScale: cs,
      mode,
      scheme: chartScheme,
    }),
  };
}

export function stopToColor(
  stop: ThemeColorStop,
  mode: EffectiveThemeMode,
): string {
  const cs = MODE_CURVES[mode].chromaScale;
  return oklch(0.65, (stop.chroma / 100) * 0.2 * cs, stop.hue);
}
